import type { ClipData } from './types';
import { BLEND_MODE_INDEX, SHAPE_TYPES, type ChromaKey, type MaskSpec, type ShapeProps } from './compositing';
import { sanitizeEffects, sanitizeGrade, type GradeData, type VideoEffects } from './grade';
import { isTransitionEasing } from './easing';
import { nodeIsPlain, sanitizeNodes, type GradeNode } from './nodes';
import { sanitizeColorSpace, type ClipColorSpace } from './colorspace';
import { sanitizeParticles, type ParticleProps } from './particles';
import { sanitizeTextFx, type TextEffects } from './textfx';
import { sanitizePaint } from './paint';

/**
 * Extended clip settings, stored as one JSON document in the clip's `ext`
 * column so new effects need no database migration. Every field is optional;
 * absent means "off". Use `clipExt` to read and `withExt` to write.
 */
export interface ClipExt {
  /** Audio noise reduction strength, 0 (off) .. 1. */
  denoise?: number;
  /** Shape clips: what to draw. */
  shape?: ShapeProps;
  /** Particle clips: the emitter and how its particles move and look (see particles.ts). */
  particles?: ParticleProps;
  /** Chroma key (green/blue screen). */
  key?: ChromaKey;
  /** Shape mask over the clip's visible box. */
  mask?: MaskSpec;
  /** How the clip combines with what is below it (default normal). */
  blend?: string;
  /** Colour LUT (.cube asset) applied after the colour adjustments. */
  lut?: { assetId: string; intensity: number };
  /** Video stabilization strength 0..1 (applied on export). */
  stabilize?: number;
  /** Play the clip's source range backwards. */
  reverse?: boolean;
  /** Hold this source time (µs) as a still frame for the whole clip. */
  freezeUs?: number;
  /**
   * Captions: timing of each word of the text (clip-local µs), and the colour
   * the word being spoken is drawn in (null = no highlight).
   */
  captions?: { words: { s: number; e: number }[]; highlight: string | null };
  /** Template field: batch exports replace this clip's text or media (see templates.ts). */
  variable?: string;
  /** Motion track this clip is pinned to (its position keyframes follow it; re-pinned after reframing). */
  follow?: string;
  /** Colour grade: wheels, curves, hue curves… (see grade.ts); applied after the basic colour adjustments. */
  grade?: GradeData;
  /**
   * Serial colour nodes (see nodes.ts), replacing `grade` when the clip has
   * more than one plain node. Keyframed grade properties animate the first.
   */
  nodes?: GradeNode[];
  /** Input colour space (camera log / HDR → the Rec.709 working space; see colorspace.ts). */
  colorSpace?: ClipColorSpace;
  /** Film grain, glow, chromatic aberration and lens distortion (see grade.ts). */
  fx?: VideoEffects;
  /**
   * Easing of the progress of the clip's incoming (`in`) and outgoing (`out`)
   * transition (see TRANSITION_EASINGS); absent = linear. Kept here rather than
   * on transitionIn/transitionOut so existing databases need no migration.
   */
  transitionEasing?: { in?: string; out?: string };
  /**
   * Where the incoming transition sits at its cut (see TransitionAlign in
   * timeline.ts): 'start' | 'end' | a custom centre offset in µs; absent = centred.
   */
  transitionAlign?: { in?: 'start' | 'end' | number };
  /**
   * Which way a dip to black / white runs (see DipMode in timeline.ts) for the
   * incoming / outgoing transition; absent = through the colour at a cut,
   * into the colour at a clip's end and out of it at its start.
   */
  transitionDip?: { in?: 'through' | 'to' | 'from'; out?: 'through' | 'to' | 'from' };
  /** Text clips: gradients, extra outlines, glow, extrusion… (see textfx.ts). */
  textFx?: TextEffects;
  /** Audio crossfade curve of the incoming / outgoing transition ('power' | 'exp'; absent = constant gain, as older transitions). */
  audioCrossfade?: { in?: 'power' | 'gain' | 'exp'; out?: 'power' | 'gain' | 'exp' };
  /** Shape of the audio fade in / out ('linear' when absent; see FADE_SHAPES in timeline.ts). */
  audioFadeShape?: { in?: FadeShape; out?: FadeShape };
  /** Disabled clips stay on the timeline but are neither seen nor heard (preview and export). */
  disabled?: boolean;
}

export const FADE_SHAPES = ['linear', 'fastStart', 'fastEnd', 'power', 'ease', 'scurve', 'fastEnds'] as const;
export type FadeShape = (typeof FADE_SHAPES)[number];

const MAX_EXT_CHARS = 192_000;

/** Template field names: a letter, then letters, digits, _ or -. */
export const VARIABLE_NAME = /^[A-Za-z][\w-]{0,39}$/;

const num = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined;

/** Keeps known fields with valid values only (unknown keys are dropped). */
export function sanitizeExt(raw: unknown): ClipExt {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: ClipExt = {};
  const denoise = num(r.denoise, 0, 1);
  if (denoise) out.denoise = denoise;
  const color = (v: unknown, fallback: string) => (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : v === '' ? '' : fallback);
  const obj = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : null);
  const sh = obj(r.shape);
  if (sh) {
    out.shape = {
      type: (SHAPE_TYPES as readonly string[]).includes(sh.type as string) ? (sh.type as ShapeProps['type']) : 'rectangle',
      width: num(sh.width, 1, 8000) ?? 600,
      height: num(sh.height, 1, 8000) ?? 120,
      fill: sh.fill === '' ? '' : sanitizePaint(sh.fill) ?? '#ffffff',
      stroke: sanitizePaint(sh.stroke) ?? '#000000',
      strokeWidth: num(sh.strokeWidth, 0, 500) ?? 0,
      radius: num(sh.radius, 0, 4000) ?? 0,
    };
  }
  if (obj(r.particles)) out.particles = sanitizeParticles(r.particles);
  const k = obj(r.key);
  if (k) {
    out.key = {
      color: color(k.color, '#00ff00') || '#00ff00',
      similarity: num(k.similarity, 0.01, 1) ?? 0.3,
      smoothness: num(k.smoothness, 0, 1) ?? 0.1,
      spill: num(k.spill, 0, 1) ?? 0.5,
    };
  }
  const m = obj(r.mask);
  if (m) {
    out.mask = {
      shape: m.shape === 'rectangle' ? 'rectangle' : 'ellipse',
      x: num(m.x, -1, 2) ?? 0.1,
      y: num(m.y, -1, 2) ?? 0.1,
      width: num(m.width, 0.001, 3) ?? 0.8,
      height: num(m.height, 0.001, 3) ?? 0.8,
      feather: num(m.feather, 0, 1) ?? 0,
      invert: m.invert === true,
    };
  }
  if (typeof r.blend === 'string' && r.blend !== 'normal' && r.blend in BLEND_MODE_INDEX) out.blend = r.blend;
  const l = obj(r.lut);
  if (l && typeof l.assetId === 'string' && l.assetId.length > 0 && l.assetId.length <= 64) {
    out.lut = { assetId: l.assetId, intensity: num(l.intensity, 0, 1) ?? 1 };
  }
  const stabilize = num(r.stabilize, 0, 1);
  if (stabilize) out.stabilize = stabilize;
  if (r.reverse === true) out.reverse = true;
  if (r.disabled === true) out.disabled = true;
  const cap = obj(r.captions);
  if (cap && Array.isArray(cap.words)) {
    const words = (cap.words as unknown[]).slice(0, 400).flatMap((w) => {
      const o = obj(w);
      const s = num(o?.s, 0, 1e12);
      const e = num(o?.e, 0, 1e12);
      return s !== undefined && e !== undefined ? [{ s: Math.round(s), e: Math.round(Math.max(s, e)) }] : [];
    });
    out.captions = { words, highlight: typeof cap.highlight === 'string' && /^#[0-9a-fA-F]{6}$/.test(cap.highlight) ? cap.highlight.toLowerCase() : null };
  }
  if (typeof r.variable === 'string' && VARIABLE_NAME.test(r.variable)) out.variable = r.variable;
  if (typeof r.follow === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(r.follow)) out.follow = r.follow;
  const freeze = num(r.freezeUs, 0, 1e13);
  if (freeze !== undefined) out.freezeUs = Math.round(freeze);
  const nodes = sanitizeNodes(r.nodes);
  if (nodes && !(nodes.length === 1 && nodeIsPlain(nodes[0]))) out.nodes = nodes;
  else {
    // One plain node is stored the way single grades always were.
    const grade = nodes ? nodes[0].grade : sanitizeGrade(r.grade);
    if (grade) out.grade = grade;
  }
  const cs = sanitizeColorSpace(r.colorSpace);
  if (cs) out.colorSpace = cs;
  const fx = sanitizeEffects(r.fx);
  if (fx) out.fx = fx;
  const textFx = sanitizeTextFx(r.textFx);
  if (textFx) out.textFx = textFx;
  const te = obj(r.transitionEasing);
  if (te) {
    const pick = (v: unknown) => (typeof v === 'string' && v !== 'linear' && v.length <= 64 && isTransitionEasing(v) ? v : undefined);
    const tin = pick(te.in);
    const tout = pick(te.out);
    if (tin || tout) out.transitionEasing = { ...(tin ? { in: tin } : {}), ...(tout ? { out: tout } : {}) };
  }
  const ta = obj(r.transitionAlign);
  if (ta) {
    const a = ta.in === 'start' || ta.in === 'end' ? ta.in : typeof ta.in === 'number' && Number.isFinite(ta.in) ? Math.round(Math.max(-1e9, Math.min(1e9, ta.in))) : undefined;
    if (a !== undefined) out.transitionAlign = { in: a };
  }
  const td = obj(r.transitionDip);
  if (td) {
    const pick = (v: unknown) => (v === 'through' || v === 'to' || v === 'from' ? v : undefined);
    const din = pick(td.in);
    const dout = pick(td.out);
    if (din || dout) out.transitionDip = { ...(din ? { in: din } : {}), ...(dout ? { out: dout } : {}) };
  }
  const ac = obj(r.audioCrossfade);
  if (ac) {
    // 'gain' is kept when set explicitly: it differs from unset only in intent, both render linear.
    const pick = (v: unknown) => (v === 'power' || v === 'gain' || v === 'exp' ? v : undefined);
    const cin = pick(ac.in);
    const cout = pick(ac.out);
    if (cin || cout) out.audioCrossfade = { ...(cin ? { in: cin } : {}), ...(cout ? { out: cout } : {}) };
  }
  const fs = obj(r.audioFadeShape);
  if (fs) {
    const pick = (v: unknown) => (typeof v === 'string' && v !== 'linear' && (FADE_SHAPES as readonly string[]).includes(v) ? v : undefined);
    const fin = pick(fs.in);
    const fout = pick(fs.out);
    if (fin || fout) out.audioFadeShape = { ...(fin ? { in: fin as FadeShape } : {}), ...(fout ? { out: fout as FadeShape } : {}) };
  }
  return out;
}

export function parseExt(json: string | undefined | null): ClipExt {
  if (!json) return {};
  try {
    return sanitizeExt(JSON.parse(json));
  } catch {
    return {};
  }
}

/** Canonical JSON of an ext document ('' when everything is off). */
export function serializeExt(ext: ClipExt): string {
  const clean = sanitizeExt(ext);
  if (!Object.keys(clean).length) return '';
  const s = JSON.stringify(clean);
  if (s.length > MAX_EXT_CHARS) throw new Error('Clip settings are too large');
  return s;
}

export function clipExt(c: Pick<ClipData, 'ext'>): ClipExt {
  return parseExt(c.ext);
}

const readCache = new Map<string, ClipExt>();

/**
 * Like clipExt, but parsed once per distinct document: for hot paths that
 * read settings every frame (keyframe base values, the renderers). The
 * result is shared, so it must not be modified.
 */
export function readExt(c: Partial<Pick<ClipData, 'ext'>>): Readonly<ClipExt> {
  const json = c.ext;
  if (!json) return EMPTY_EXT;
  let hit = readCache.get(json);
  if (!hit) {
    if (readCache.size > 500) readCache.clear();
    hit = parseExt(json);
    readCache.set(json, hit);
  }
  return hit;
}
const EMPTY_EXT: ClipExt = {};

/** Whether a clip is switched off (kept on the timeline, left out of the picture and the sound). */
export function clipDisabled(c: Partial<Pick<ClipData, 'ext'>>): boolean {
  return readExt(c).disabled === true;
}

/** A copy of the clip with ext fields merged in (undefined removes a field). */
export function withExt<C extends Pick<ClipData, 'ext'>>(c: C, patch: Partial<ClipExt>): C {
  const next: Record<string, unknown> = { ...clipExt(c) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete next[k];
    else next[k] = v;
  }
  return { ...c, ext: serializeExt(next as ClipExt) };
}
